import { Platform, LogBox, Dimensions } from "react-native";
import Config from "react-native-config";
import dayjs from "dayjs";
import jalaliday from "jalaliday";
import relativeTime from "dayjs/plugin/relativeTime";
import axios, { AxiosError } from "axios";
import * as Sentry from "sentry-expo";
import { RewriteFrames as RewriteFramesIntegration } from "@sentry/integrations";
import store, { persistor } from "../configs/store-config";
import globalActions from "../ducks/global/actions";
import userSelectors from "../ducks/user/selectors";
import metrix from "../libs/metrix";
import theme from "../configs/theme-config";
import { space } from "@sloth-system/styled-utils";

export function getEnv(key: string) {
  let isNext = Config.REACT_APP_ENVIRONMENT !== "prod";
  let hardcodedProductionEnv = isNext
    ? {}
    : {
        REACT_APP_ENVIRONMENT: "prod",
        REACT_APP_VERSION: "0.10.0",
        REACT_APP_API_URL: "https://api.digiwash.ir/api/v1",
        REACT_APP_PAYMENT_CALLBACK_URL: "https://charge.digiwash.app",
        REACT_APP_GRAPHQL_API_URL: "https://api.digiwash.app",
      };
  let val = hardcodedProductionEnv[key] || Config[key] || process?.env[key];
  return val;
}

export function sourcePlatform() {
  if (Platform.OS === "android") {
    return "android";
  }
  if (Platform.OS === "web") {
    return "pwa";
  }
  if (Platform.OS === "ios") {
    return "ios";
  }

  return undefined;
}

// const SENTRY_DSN =
//   "https://ecd825aabeda419a8cd3c84a52769f50@sentry.digiwash.ir/7";
// const SENTRY_ENVIROMENT = `${getEnv(
//   "REACT_APP_ENVIRONMENT"
// )}-${sourcePlatform()}`;

function prepareWebEnv() {
  let isProd = Config.REACT_APP_ENVIRONMENT === "prod";

  if (Platform.OS === "web") {
    if (isProd) {
      // @ts-ignore
      window.Metrix = metrix.initialize({
        appId: "utkvltsmoimoaei",
      });
    }
  }
}

function prepareNativeEnv() {
  if (Platform.OS === "ios" || Platform.OS === "android") {
    LogBox.ignoreAllLogs(true);

    if (__DEV__) {
      // TRICK FOR TS
      // @ts-ignore
      // let global: any = global;
      // @ts-ignore
      let window: any = window || {};

      // -----

      global.XMLHttpRequest = global.originalXMLHttpRequest
        ? global.originalXMLHttpRequest
        : global.XMLHttpRequest;

      global.FormData = global.originalFormData
        ? global.originalFormData
        : global.FormData;

      fetch; // Ensure to get the lazy property

      if (window.__FETCH_SUPPORT__) {
        // it's RNDebugger only to have
        window.__FETCH_SUPPORT__.blob = false;
      } else {
        /*
         * Set __FETCH_SUPPORT__ to false is just work for `fetch`.
         * If you're using another way you can just use the native Blob and remove the `else` statement
         */
        global.Blob = global.originalBlob ? global.originalBlob : global.Blob;
        global.FileReader = global.originalFileReader
          ? global.originalFileReader
          : global.FileReader;
      }
    }
  }
}

export function prepareEnv() {
  dayjs.extend(jalaliday);
  dayjs.extend(relativeTime);
  dayjs.calendar("jalali").locale("fa");

  // Add a request interceptor
  axios.defaults.headers["X-DEVICE-TYPE"] = sourcePlatform();
  axios.interceptors.request.use(
    function (config) {
      config.headers["Accept-Language"] = "fa";
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor
  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      let errorCode =
        error && error.response && error.response.status
          ? error.response.status
          : undefined;

      if (errorCode === 401) {
        let hasToken = userSelectors.getToken(store.getState());
        if (hasToken) {
          store.dispatch(globalActions.logout());
          persistor.flush();
        }
      }

      return Promise.reject(error);
    }
  );

  // Sentry setup
  // Sentry.init({
  //   dsn: SENTRY_DSN,
  //   environment: SENTRY_ENVIROMENT,
  //   // enableInExpoDevelopment: true,
  //   // debug: true, // Sentry will try to print out useful debugging information if something goes wrong with sending an event. Set this to `false` in production.
  //   integrations: [
  //     new RewriteFramesIntegration({
  //       iteratee: (frame) => {
  //         if (frame.filename) {
  //           // the values depend on what names you give the bundle files you are uploading to Sentry
  //           frame.filename =
  //             Platform.OS === "android"
  //               ? "app:///index.android.bundle"
  //               : "app:///main.jsbundle";
  //         }
  //         return frame;
  //       },
  //     }),
  //   ],
  // });

  prepareNativeEnv();
  prepareWebEnv();
}

// Detects if device is on iOS
export function isIos() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}
// Detects if device is in standalone mode
export function isInStandaloneMode() {
  return "standalone" in window.navigator && window.navigator.standalone;
}

export function iosSafeArePadding() {
  if (Platform.OS === "web") {
    if (isInStandaloneMode()) {
      return 16;
    }
    return "calc(env(safe-area-inset-bottom))";
  }
  return null;
}

interface GQLErrorResponseType {
  data: null;
  errors: ErrorType[];
  status: number;
}
interface ErrorType {
  extensions: {
    code: string;
    exception: {
      stacktrace: string[];
    };
  };
  locations: locations[];
  message: string;
  path: string[];
}
interface locations {
  column: number;
  line: number;
}

export function formatError(response: GQLErrorResponseType) {
  let output = [];
  if (response?.errors) {
    output = response.errors.map((error) => {
      return error.message;
    });
  }

  return output.join(`\n`) || "مشکلی در ارتباط با سرور پیش آمده است.";
}

export function isTablet() {
  let windowWidth = Dimensions.get("window").width;
  if (windowWidth >= 768) {
    return true;
  } else {
    return false;
  }
}
export function pagePadding() {
  let windowWidth = Dimensions.get("window").width;
  if (isTablet()) {
    return windowWidth - 768;
  } else {
    return 0;
  }
}

export function pxValueToNumber(value) {
  function getSum(total, num) {
    return total + num;
  }
  let t = [];
  if (value) {
    value.forEach((item) => {
      let [val] = space(item)({
        theme: theme,
      }).split("p");
      t.push(Number(val));
    });
  }
  return t.reduce(getSum, 0);
}

export function boxRatioWidth({
  columns,
  padding,
}: {
  columns: number;
  padding?: string[];
}) {
  let windowWidth = Dimensions.get("window").width;
  let emptySpaces;
  if (padding) {
    emptySpaces = pxValueToNumber(padding) + pagePadding();
  } else {
    emptySpaces = pagePadding();
  }

  let width = (windowWidth - emptySpaces) / columns;

  return width;
}

export function suggestResponsiveBoxRatioForCategories() {
  let windowWidth = Dimensions.get("window").width;
  if (Platform.OS === "web") {
    if (windowWidth >= 768) {
      return { column: 4, padding: ["huge", "xxxl", "xxxl"] };
    } else if (windowWidth < 768 && windowWidth > 468) {
      return { column: 3, padding: ["huge", "xxxl", "m"] };
    } else {
      return { column: 2, padding: ["huge", "l"] };
    }
  } else {
    return { column: 2, padding: ["huge", "l"] };
  }
}

export function suggestResponsiveBoxRatioForServices() {
  let windowWidth = Dimensions.get("window").width;
  if (Platform.OS === "web") {
    if (windowWidth >= 768) {
      return { column: 3, padding: ["xxxl", "xxxl"] };
    } else {
      return { column: 2, padding: ["xxxl", "l"] };
    }
  } else {
    return { column: 2, padding: ["xxxl", "l"] };
  }
}

export function suggestResponsiveColumnForCategories(columns) {
  if (columns === 4) {
    return ["22.5%", "22.5%", "22.5%", "22.5%"];
  } else if (columns === 3) {
    return ["30%", "30%", "30%"];
  } else {
    return ["45%", "45%"];
  }
}
export function suggestResponsiveColumnForServices(columns) {
  if (columns === 3) {
    return ["32%", "32%", "32%"];
  } else {
    return ["48%", "48%"];
  }
}

interface GqlErrorType {
  response: {
    errors: {
      message: string;
      extensions: {
        code: string;
        exception: {
          stacktrace: string[];
        };
      };
      locations: {
        column: number;
        line: number;
      }[];
    }[];
    status: number;
  };
  request: {
    query: string;
  };
}

export function formatAndRaiseError(error: GqlErrorType): string;
export function formatAndRaiseError(error: AxiosError): string;
export function formatAndRaiseError(error: Error): string;
export function formatAndRaiseError(error: any): string {
  let errors: string[] = [];

  let isLegacyError =
    !!error?.response?.data &&
    (error?.response?.errors?.length === 0 || !error?.response?.errors);
  let isApiError = error?.response?.errors?.length > 0;
  let isCustomError = error instanceof Error;
  if (isLegacyError) {
    // For assigning type we couldn't find any solution, so we are using redeclaring variable by the desired type.
    let e: AxiosError = error;
    errors.push(e?.response?.data?.message);
  } else if (isApiError) {
    // For assigning type we couldn't find any solution, so we are using redeclaring variable by the desired type.
    let e: GqlErrorType = error;
    errors.push(e?.response?.errors[0]?.message);
  } else if (isCustomError) {
    // For assigning type we couldn't find any solution, so we are using redeclaring variable by the desired type.
    let e: Error = error;
    errors.push(e.message);
  } else {
    throw new Error("Uknown Error");
  }

  throw errors.join(`\n`);
}
